import React, { useState, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { FileTextIcon, InfoCircleIcon } from '@vlabs/icons';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';
import { selectAppServices } from '@vlabs/shared/selectors/appSelectors';
import {
  Table, Panel, Divider, Tooltip,
} from '@vlabs/uikit';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import st from './InfoPage.module.sass';

function StateLabelCell({ value }) {
  const { t } = useTranslation();

  const stateList = {
    loading: t('info:stateList.loading'),
    loaded: t('info:stateList.loaded'),
    failed: t('info:stateList.failed'),
  };

  return (
    <React.Fragment>
      <div className={cn(st.State, st[`State_${value}`])} />
      <span>{stateList[value]}</span>
    </React.Fragment>
  );
}

StateLabelCell.propTypes = {
  value: PropTypes.string,
};

StateLabelCell.defaultProps = {
  value: undefined,
};

function HealthCheckCell({ value }) {
  const [response, setResponse] = useState({});
  const { t } = useTranslation();

  const isGoodResponse = [200, 201, 202, 204, 205, 206, 207, 208, 226].includes(response?.status);

  const services = {
    clementineAuth: 'clementineAuthClient',
    lunaStreams: 'lunaStreamsClient',
    lunaPlatform: 'lunaClient',
    vlNotifier: 'notifierClient',
    vlTimetracker: 'timeTrackerClient',
    clementineDepartments: 'clementineDepartmentsClient',
    vaReporter: 'vaReporterClient',
    vlAccess: 'vlAccessClient',
  };

  useEffect(() => {
    if (!services[value]) return;

    const fetchHealthCheck = async () => {
      try {
        const result = await apiContainer[services[value]]?.healthcheck();
        setResponse({ status: result?.status, data: result?.data });
      } catch (error) {
        setResponse({ status: error?.response });
      }
    };

    fetchHealthCheck();
    const interval = setInterval(fetchHealthCheck, 1800000);
    /* eslint-disable consistent-return */
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (value === 'ui' || value === 'clementineUI' || response?.status === undefined) return null;

  return (
    <div className={st.HealthCheck}>
      <span>{t(`info:healthcheck.${isGoodResponse ? 'goodresponse' : 'badresponse'}`)}</span>
      {!!isGoodResponse && !!response.data?.execution_time && (
        <Tooltip
          className="Tooltip_ui"
          data-testid="executionTimeTooltip"
          data-tooltip-content={t('info:healthcheck.executiontime', { value: response.data.execution_time })}
        >
          <InfoCircleIcon className="Tooltip_icon" />
        </Tooltip>
      )}
      {!isGoodResponse && !!(response.error_code || response.errors) && (
        <Tooltip
          className="Tooltip_ui"
          data-testid="badResponseTooltip"
          data-tooltip-content={JSON.stringify(response)}
        >
          <InfoCircleIcon className="Tooltip_icon" />
        </Tooltip>
      )}
    </div>
  );
}

HealthCheckCell.propTypes = {
  value: PropTypes.string,
};

HealthCheckCell.defaultProps = {
  value: undefined,
};

function UIDocsCell({ value }) {
  const { t } = useTranslation();

  if (value === undefined) return null;
  return (
    <a
      aria-label="FileTextIcon"
      className={st.DocsLink}
      href={value}
      title={t('view_action')}
    >
      <FileTextIcon />
    </a>
  );
}

UIDocsCell.propTypes = {
  value: PropTypes.string,
};

UIDocsCell.defaultProps = {
  value: undefined,
};

function InfoPageComponent({
  services,
  showDocumentationColumn,
}) {
  const { t } = useTranslation();
  const data = Object.values(services);

  const columns = [
    { Header: t('info:table.columns.title'), id: 'title', accessor: (row) => t(`info:table.services.${row.name}`) },
    { Header: t('info:table.columns.version'), accessor: 'version' },
    { Header: t('info:table.columns.state'), accessor: 'state', Cell: StateLabelCell },
    { Header: t('info:table.columns.healthcheck'), accessor: 'name', Cell: HealthCheckCell },
  ];

  if (showDocumentationColumn) {
    columns.push({
      Header: t('info:table.columns.documentation'),
      accessor: 'uiDocsURL',
      Cell: UIDocsCell,
    });
  }

  return (
    <Page>
      <PageContent>
        <PageHeader title={t('info:title')} />
        <Panel className="PanelAutoHeight">
          <Table
            columns={columns}
            data={data}
          />
        </Panel>
      </PageContent>
    </Page>
  );
}

InfoPageComponent.propTypes = {
  services: PropTypes.shape({}),
  showDocumentationColumn: PropTypes.bool,
};

InfoPageComponent.defaultProps = {
  services: {},
  showDocumentationColumn: true,
};

export const InfoPage = connect((state) => ({
  services: selectAppServices(state),
}))(InfoPageComponent);
